"use client";
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../../../../utils/axiosInstance";
import { API_ENDPOINTS } from "../../../../constants/config";
import { getTeacherProfileUrl, findUserByUsername } from "../../../../utils/urlUtils";
import * as XLSX from "xlsx";

export const formatAddress = (address) => {
  if (!address) return "Not provided";
  const { street, city, state, pincode } = address;
  return [street, city, state, pincode].filter(Boolean).join(", ");
};

export const getDesignationName = (designation) => {
  if (!designation) return "Not assigned";
  if (typeof designation === "object") return designation.name || "Not assigned";
  return designation;
};

const getDepartmentName = (department) => {
  if (!department) return "Not assigned";
  if (typeof department === "object") return department.name || "Not assigned";
  return department;
};

const getWingName = (wing) => {
  if (!wing) return "Not assigned";
  if (typeof wing === "object") return wing.name || "Not assigned";
  return wing;
};

export const getDepartmentIds = (teacher) => {
  if (Array.isArray(teacher?.departments) && teacher.departments.length > 0) {
    return teacher.departments
      .map((item) => (typeof item === "object" ? item?._id : item))
      .filter(Boolean)
      .map(String);
  }
  if (!teacher?.department) return [];
  const id = typeof teacher.department === "object" ? teacher.department?._id : teacher.department;
  return id ? [String(id)] : [];
};

export const getWingIds = (teacher) => {
  if (Array.isArray(teacher?.wings) && teacher.wings.length > 0) {
    return teacher.wings
      .map((item) => (typeof item === "object" ? item?._id : item))
      .filter(Boolean)
      .map(String);
  }
  if (!teacher?.wing) return [];
  const id = typeof teacher.wing === "object" ? teacher.wing?._id : teacher.wing;
  return id ? [String(id)] : [];
};

export const getDepartmentNames = (teacher) => {
  if (Array.isArray(teacher?.departments) && teacher.departments.length > 0) {
    const names = teacher.departments
      .map((item) => (typeof item === "object" ? item?.name : String(item || "")))
      .filter(Boolean);
    if (names.length > 0) return names.join(", ");
  }
  return getDepartmentName(teacher?.department);
};

export const getWingNames = (teacher) => {
  if (Array.isArray(teacher?.wings) && teacher.wings.length > 0) {
    const names = teacher.wings
      .map((item) => (typeof item === "object" ? item?.name : String(item || "")))
      .filter(Boolean);
    if (names.length > 0) return names.join(", ");
  }
  return getWingName(teacher?.wing);
};

export const getTeacherInitial = (teacher) => {
  if (teacher.firstName) return teacher.firstName.charAt(0);
  if (teacher.name) return teacher.name.charAt(0);
  return "";
};

export function useTeachers() {
  const navigate = useNavigate();
  const { teacherName } = useParams();

  const [teachers, setTeachers] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPermissionModalOpen, setIsPermissionModalOpen] = useState(false);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [selectedTeacherProfile, setSelectedTeacherProfile] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentAcademicYear, setCurrentAcademicYear] = useState("");
  const [academicYears, setAcademicYears] = useState([]);
  const [academicYearFilter, setAcademicYearFilter] = useState("");
  const [classFilter, setClassFilter] = useState("all");
  const [isImporting, setIsImporting] = useState(false);
  const [importSuccess, setImportSuccess] = useState(false);
  const [showProgressDialog, setShowProgressDialog] = useState(false);
  const [importProgress, setImportProgress] = useState({
    totalRecords: 0,
    processedRecords: 0,
    successCount: 0,
    failureCount: 0,
  });
  const [sortColumn, setSortColumn] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");
  const [activeTab, setActiveTab] = useState("profile");
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [subjectsList, setSubjectsList] = useState([]);
  const [classesData, setClassesData] = useState([]);
  const [sectionData, setSectionsData] = useState([]);
  const [subjectFilter, setSubjectFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("active");
  const [designationFilter, setDesignationFilter] = useState("all");
  const [departmentFilter, setDepartmentFilter] = useState("all");
  const [wingFilter, setWingFilter] = useState("all");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [totalTeachers, setTotalTeachers] = useState(0);
  const [designations, setDesignations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [wings, setWings] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [isLoading, setIsLoading] = useState(true);
  const [isImportManualOpen, setIsImportManualOpen] = useState(false);

  useEffect(() => {
    fetchCurrentAcademicYear();
    fetchAcademicYears();
    fetchSubjects();
    fetchClasses();
    fetchSectionsByClass();
    fetchTeacherLeaves();
    fetchDesignations();
    fetchDepartments();
    fetchWings();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery.trim());
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    setCurrentPage(1);
  }, [academicYearFilter, classFilter, subjectFilter, statusFilter, designationFilter, departmentFilter, wingFilter, debouncedSearchQuery, sortColumn, sortDirection, itemsPerPage]);

  useEffect(() => {
    fetchTeachers();
  }, [academicYearFilter, classFilter, subjectFilter, statusFilter, designationFilter, departmentFilter, wingFilter, debouncedSearchQuery, sortColumn, sortDirection, currentPage, itemsPerPage]);

  useEffect(() => {
    if (importSuccess) {
      const timer = setTimeout(() => setImportSuccess(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [importSuccess]);

  useEffect(() => {
    if (!teacherName) return;
    const teacher = findUserByUsername(teachers, teacherName);
    if (teacher) {
      setSelectedTeacherProfile(teacher);
      return;
    }
    if (teachers.length === 0) return;
    let cancelled = false;
    const token = localStorage.getItem("token");
    axiosInstance
      .get(API_ENDPOINTS.usersTeachers, {
        headers: { Authorization: `Bearer ${token}` },
        params: { academicYear: academicYearFilter, status: "all", search: teacherName, paginate: true, limit: 50 },
      })
      .then((res) => {
        if (cancelled) return;
        const list = (res.data?.data?.teachers || []).filter((u) => u.role === "teacher");
        const found = findUserByUsername(list, teacherName);
        if (found) setSelectedTeacherProfile(found);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [teacherName, teachers, academicYearFilter]);

  const fetchTeacherLeaves = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) { setError("Please log in again"); return; }
      const response = await axiosInstance.get(API_ENDPOINTS.leaveTeacher, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) setLeaves(response.data.data);
    } catch (error) {
      if (error.response?.status === 403) {
        setError("Access denied. Please log in again.");
      } else {
        setError("Failed to fetch leaves");
      }
    }
  };

  const handleViewProfile = (teacher) => {
    navigate(getTeacherProfileUrl(teacher), { replace: true });
    setSelectedTeacherProfile(teacher);
  };

  const fetchTeachers = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem("token");
      const response = await axiosInstance.get(API_ENDPOINTS.usersTeachers, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          academicYear: academicYearFilter,
          status: statusFilter,
          search: debouncedSearchQuery || undefined,
          class: classFilter === "all" ? undefined : classFilter,
          subject: subjectFilter === "all" ? undefined : subjectFilter,
          designation: designationFilter === "all" ? undefined : designationFilter,
          department: departmentFilter === "all" ? undefined : departmentFilter,
          wing: wingFilter === "all" ? undefined : wingFilter,
          sortBy: sortColumn,
          sortOrder: sortDirection,
          paginate: true,
          page: currentPage,
          limit: itemsPerPage,
        },
      });

      const allTeachers = (response.data?.data?.teachers || []).filter((user) => user.role === "teacher");
      const teachersWithDetails = allTeachers.map((teacher) => {
        const classTeacherAssignment = teacher.classAssignments?.find((a) => a.type === "class_teacher");
        if (classTeacherAssignment) {
          return {
            ...teacher,
            classTeacherDetails: {
              ...classTeacherAssignment,
              className: classTeacherAssignment.class?.name || "",
              sectionName: classTeacherAssignment.section?.name || "",
            },
          };
        }
        return teacher;
      });

      setTeachers(teachersWithDetails);
      setTotalTeachers(Number(response.data?.data?.count || 0));
    } catch (error) {
      console.error("Failed to fetch teachers:", error);
      setError("Failed to fetch teachers");
      setTeachers([]);
      setTotalTeachers(0);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDesignations = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axiosInstance.get(API_ENDPOINTS.designations, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDesignations(response.data?.data?.designations || []);
    } catch (error) {
      console.error("Failed to fetch designations:", error);
    }
  };

  const fetchDepartments = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axiosInstance.get(API_ENDPOINTS.departments, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDepartments(response.data?.data?.departments || []);
    } catch (error) {
      console.error("Failed to fetch departments:", error);
    }
  };

  const fetchWings = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axiosInstance.get(API_ENDPOINTS.wings, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setWings(response.data?.data?.wings || []);
    } catch (error) {
      console.error("Failed to fetch wings:", error);
    }
  };

  const fetchSectionsByClass = async (className) => {
    try {
      const classObj = classesData.find((cls) => cls.name === className);
      if (!classObj) return;
      const token = localStorage.getItem("token");
      const response = await axiosInstance.get(API_ENDPOINTS.sectionsByClass(classObj._id), {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSectionsData(response.data.data.sections);
    } catch (error) {
      console.error("Failed to fetch sections:", error);
    }
  };

  const fetchClasses = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axiosInstance.get(API_ENDPOINTS.classes, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setClassesData(response.data.data.classes);
    } catch (error) {
      console.error("Failed to fetch classes:", error);
      setError("Failed to fetch classes");
    }
  };

  const fetchSubjects = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axiosInstance.get(API_ENDPOINTS.subjects, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSubjectsList(response.data.data.subjects);
    } catch (error) {
      console.error("Failed to fetch subjects:", error);
      setError("Failed to fetch subjects");
    }
  };

  const fetchAcademicYears = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axiosInstance.get(API_ENDPOINTS.academicYears, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAcademicYears(response.data.data.academicYears);
    } catch (error) {
      console.error("Failed to fetch academic years:", error);
    }
  };

  const fetchCurrentAcademicYear = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axiosInstance.get(API_ENDPOINTS.usersAcademicYear, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const year = response.data.data.academicYear._id;
      setCurrentAcademicYear(year);
      setAcademicYearFilter(year);
    } catch (error) {
      setError("Failed to fetch current academic year");
    }
  };

  const handleUpdateTeacher = async (teacherId, updates) => {
    try {
      const token = localStorage.getItem("token");
      await axiosInstance.put(
        API_ENDPOINTS.user(teacherId),
        { ...updates, academicYear: updates.academicYear || currentAcademicYear },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchTeachers();
      setIsEditModalOpen(false);
      setSelectedTeacher(null);
      setSuccessMessage("Teacher updated successfully");
    } catch (error) {
      setError(error.response?.data?.message || "Failed to update teacher");
    }
  };

  const handleDeleteTeacher = async (teacherId) => {
    try {
      const token = localStorage.getItem("token");
      await axiosInstance.delete(API_ENDPOINTS.user(teacherId), {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchTeachers();
      setSuccessMessage("Teacher deleted successfully");
    } catch (error) {
      setError(error.response?.data?.message || "Failed to delete teacher");
    }
  };

  const getFileRowCount = async (file) => {
    return new Promise((resolve, reject) => {
      const ext = file.name.split(".").pop().toLowerCase();
      if (ext === "xlsx" || ext === "xls") {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: "array" });
            const worksheet = workbook.Sheets[workbook.SheetNames[0]];
            resolve(XLSX.utils.sheet_to_json(worksheet).length);
          } catch (err) { reject(err); }
        };
        reader.onerror = reject;
        reader.readAsArrayBuffer(file);
      } else if (ext === "csv") {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const lines = e.target.result.split("\n").filter((l) => l.trim().length > 0);
            resolve(lines.length > 1 ? lines.length - 1 : 0);
          } catch (err) { reject(err); }
        };
        reader.onerror = reject;
        reader.readAsText(file);
      } else {
        reject(new Error("Unsupported file format"));
      }
    });
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setIsImporting(true);
    let totalRecords = 0;
    try {
      totalRecords = await getFileRowCount(file);
      setImportProgress({ totalRecords, processedRecords: 0, successCount: 0, failureCount: 0 });
      setShowProgressDialog(true);
    } catch (error) {
      console.error("Error parsing file:", error);
    }

    const formData = new FormData();
    formData.append("file", file);

    try {
      const token = localStorage.getItem("token");
      setImportProgress((prev) => ({ ...prev, processedRecords: Math.floor(totalRecords * 0.3) }));
      const response = await axiosInstance.post(API_ENDPOINTS.usersImport, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      const { imported, updated, skipped, skippedUsers, message } = response.data.data || {};
      const successCount = (imported || 0) + (updated || 0);
      const failureCount = skipped || 0;
      setImportProgress({
        totalRecords: totalRecords || (successCount + failureCount),
        processedRecords: totalRecords || (successCount + failureCount),
        successCount,
        failureCount,
      });
      setTimeout(() => {
        setShowProgressDialog(false);
        if (imported > 0 || updated > 0) {
          setImportSuccess(true);
          const parts = [];
          if (imported > 0) parts.push(`${imported} new teacher${imported !== 1 ? "s" : ""} imported`);
          if (updated > 0) parts.push(`${updated} teacher${updated !== 1 ? "s" : ""} updated`);
          setSuccessMessage(parts.join(" and ") + " successfully!");
        } else {
          setImportSuccess(false);
          if (skippedUsers?.length > 0) {
            const details = skippedUsers.map((su) => `Row ${su.row}: ${su.name || "Unknown"} - ${su.reason || "Skipped"}`).join("\n");
            setError(`No teachers were imported or updated.\n\nSkipped:\n${details}`);
          } else {
            setError(message || "No teachers were imported or updated. Please check the file format and data.");
          }
        }
      }, 500);
      fetchTeachers();
    } catch (error) {
      console.error("Error importing teachers:", error);
      setShowProgressDialog(false);
      if (error.response) {
        if (error.response.data.invalidRecords) {
          const inv = error.response.data.invalidRecords;
          setError(`Import failed: ${inv.length} records had errors.\n` + inv.map((r) => `- ${r.name}: ${r.message}`).join("\n"));
        } else {
          setError(error.response.data.message || "Failed to import teachers");
        }
      } else if (error.request) {
        setError("No response received from server. Please check your connection.");
      } else {
        setError("Error setting up request: " + error.message);
      }
    } finally {
      setIsImporting(false);
      event.target.value = "";
    }
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const fetchAllTeachersForExport = async () => {
    const token = localStorage.getItem("token");
    const response = await axiosInstance.get(API_ENDPOINTS.usersTeachers, {
      headers: { Authorization: `Bearer ${token}` },
      params: {
        academicYear: academicYearFilter,
        status: statusFilter,
        search: debouncedSearchQuery || undefined,
        class: classFilter === "all" ? undefined : classFilter,
        subject: subjectFilter === "all" ? undefined : subjectFilter,
        designation: designationFilter === "all" ? undefined : designationFilter,
        department: departmentFilter === "all" ? undefined : departmentFilter,
        wing: wingFilter === "all" ? undefined : wingFilter,
        sortBy: sortColumn,
        sortOrder: sortDirection,
      },
    });
    return response.data?.data?.teachers || [];
  };

  return {
    teachers, leaves, error, setError, successMessage, setSuccessMessage,
    isEditModalOpen, setIsEditModalOpen,
    isModalOpen, setIsModalOpen,
    isPermissionModalOpen, setIsPermissionModalOpen,
    selectedTeacher, setSelectedTeacher,
    selectedTeacherProfile, setSelectedTeacherProfile,
    searchQuery, setSearchQuery,
    currentAcademicYear, academicYears,
    academicYearFilter, setAcademicYearFilter,
    classFilter, setClassFilter,
    isImporting, importSuccess, setImportSuccess,
    showProgressDialog, importProgress,
    sortColumn, sortDirection,
    activeTab, setActiveTab,
    isDeleteDialogOpen, setIsDeleteDialogOpen,
    subjectsList, classesData, sectionData,
    subjectFilter, setSubjectFilter,
    statusFilter, setStatusFilter,
    designationFilter, setDesignationFilter,
    departmentFilter, setDepartmentFilter,
    wingFilter, setWingFilter,
    totalTeachers, designations, departments, wings,
    currentPage, setCurrentPage,
    itemsPerPage, setItemsPerPage,
    isLoading, isImportManualOpen, setIsImportManualOpen,
    fetchTeachers, fetchAllTeachersForExport,
    handleViewProfile, handleUpdateTeacher, handleDeleteTeacher,
    handleFileUpload, handleSort,
  };
}
